/**
 * How well a citation was anchored back to its source, derived from the fields
 * `resolve_anchor` already returns.
 *
 * The model proposes a passage; the server tries to find that passage in the
 * extracted page text and refuses to invent a page when it cannot. That refusal
 * is only useful if the reader can see it — otherwise a citation we failed to
 * verify looks exactly like one we confirmed, which is the failure mode worth
 * designing against here.
 */

import i18n from '@/i18n'

/** Mirrors ANCHOR_MATCH_CONFIDENCE_THRESHOLD in the backend anchor-resolve.ts. */
export const ANCHOR_MATCH_CONFIDENCE_THRESHOLD = 0.55

export type CitationVerification = 'matched' | 'page_only' | 'unlocated'

export interface CitationAnchorFields {
  pageNumber?: number | null
  pageUnknown?: boolean
  unitCharStart?: number | null
  unitCharEnd?: number | null
  anchorConfidence?: number | null
}

export function citationVerification(
  citation: CitationAnchorFields | null | undefined,
): CitationVerification {
  if (!citation) {
    return 'unlocated'
  }

  const hasPage = citation.pageUnknown !== true && citation.pageNumber != null
  if (!hasPage) {
    return 'unlocated'
  }

  const start = citation.unitCharStart
  const end = citation.unitCharEnd
  const passageLocated = start != null && end != null && end > start
  const confident =
    Number(citation.anchorConfidence ?? 0) >= ANCHOR_MATCH_CONFIDENCE_THRESHOLD

  return passageLocated && confident ? 'matched' : 'page_only'
}

export interface CitationVerificationPresentation {
  markerClassName: string
}

const VERIFICATION_COPY_KEYS: Record<
  CitationVerification,
  { label: string; description: string }
> = {
  matched: {
    label: 'threads.detail.matchedInSource',
    description: 'threads.detail.matchedInSourceHint',
  },
  page_only: {
    label: 'threads.detail.pageMatchOnly',
    description: 'threads.detail.pageMatchOnlyHint',
  },
  unlocated: {
    label: 'threads.detail.unverified',
    description: 'threads.detail.unverifiedHint',
  },
}

export function citationVerificationCopy(verification: CitationVerification): {
  label: string
  description: string
} {
  const keys = VERIFICATION_COPY_KEYS[verification]
  return {
    label: i18n.t(keys.label, { ns: 'app' }),
    description: i18n.t(keys.description, { ns: 'app' }),
  }
}

/**
 * Every state is drawn from the theme's own tokens, and all three sit in the
 * accent family. The scale lives on the inline marker and its tooltip only:
 * a banner over the passage and a badge beside the title said the same thing
 * twice more, and turned an approximate anchor into an apparent fault.
 *
 * All three sit in the accent family: a citation we could not verify is a caveat, not a failure, and
 * a red chip mid-sentence reads as an error in the answer itself. Confidence is
 * carried by fill weight instead — solid, tinted, then outline — so the scale
 * survives a rebrand and both colour schemes without any hardcoded hues.
 */
export const CITATION_VERIFICATION: Record<
  CitationVerification,
  CitationVerificationPresentation
> = {
  matched: {
    markerClassName:
      'border border-transparent bg-primary text-primary-foreground hover:bg-primary/90',
  },
  page_only: {
    markerClassName:
      'border border-primary/30 bg-primary/15 text-primary hover:bg-primary/25',
  },
  unlocated: {
    markerClassName:
      'border border-dashed border-primary/50 bg-transparent text-muted-foreground hover:bg-primary/10 hover:text-foreground',
  },
}
